<template>
  <div>
    <FetchingOverlay v-if="isLoading" />
    <template v-if="orders.length > 0">
      <div class="header">
        Order Summary
        <span v-if="orders.length > 1">({{ orders.length }} items)</span>
      </div>

      <!-- Summary Items -->
      <Scrollbar
        class="scrollbar scrollbar--primary"
        :style="{
          '--primary': theme.primary,
          '--secondary': theme.secondary
        }"
      >
        <div class="summary">
          <OrderItem
            v-for="(order, i) of orders"
            :key="i"
            :index="i"
            :order="order"
            @onEditSchedule="onEditSchedule"
          />
        </div>
      </Scrollbar>

      <!-- Summary Total -->
      <OrderTotal :orders="orders" />

      <!-- Action Buttons -->
      <div class="justify-content-end mt-30 footer-action">
        <ActionButton
          class="w-auto d-inline-block"
          mode="outline"
          label="Add Booking"
          :theme="{ primary, secondary, event, danger }"
          @customEvent="openMakeBooking"
        />
        <CheckoutButton
          @fetching="handleLoading"
          :user="user"
          class="btn--checkout"
          @onEditSchedule="onEditSchedule"
        />
      </div>
    </template>

    <div class="row" v-else>
      <div class="col-12">
        <div class="no-result">
          <img src="@/assets/media/space.svg" alt="Space BG" />
          <template v-if="isLoading">
            <p>Please wait...</p>
          </template>
          <template v-else>
            <p>There are no items in this cart.</p>
            <ActionButton
              class="w-auto mx-auto px-4 py-3"
              mode="outline"
              :theme="{ primary, secondary, event, danger }"
              @customEvent="openMakeBooking"
              label="Make a Booking"
            />
          </template>
        </div>
      </div>
    </div>

    <MakeBookingModal
      v-if="isOpenMakeBooking"
      @close="isOpenMakeBooking = false"
      :userId="this.$route.params.id"
    />

    <NewModalBooking
      v-if="isOpenNewBooking"
      @close="onCloseModal"
      @submit="onSubmitModal"
      :data="modalData"
      :orderIndex="orderIndex"
      :order="orderData"
    ></NewModalBooking>
  </div>
</template>

<script>
import ActionButton from "@/components/ActionButton";
import FetchingOverlay from "@/components/FetchingOverlay";
import Scrollbar from "@/components/Scrollbar";
import CheckoutButton from "@/components/booking/CheckoutButton";
import NewModalBooking from "@/components/booking/NewModalBooking";
import MakeBookingModal from "@/components/modals/MakeBookingModal";
import axios from "@/helpers/axios";
import { mapActions, mapGetters } from "vuex";
import OrderItem from "./OrderItem";
import OrderTotal from "./OrderTotal";
export default {
  name: "OrderSummary",
  components: {
    CheckoutButton,
    FetchingOverlay,
    MakeBookingModal,
    NewModalBooking,
    OrderItem,
    OrderTotal,
    Scrollbar,
    ActionButton
  },
  computed: {
    orders() {
      return this.getOrders();
    },
    theme() {
      return this.$store.state.app.scheme;
    },
    primary() {
      return this.$store.state.app.scheme.primary;
    },
    secondary() {
      return this.$store.state.app.scheme.secondary;
    },
    danger() {
      return this.$store.state.app.scheme.danger;
    },
    event() {
      return this.$store.state.app.scheme.event;
    }
  },
  props: {
    user: {
      type: Object,
      default: null
    }
  },
  data: () => ({
    isLoading: false,
    isOpenMakeBooking: false,
    isOpenNewBooking: false,

    orderData: null,
    orderIndex: null,
    modalData: null,
    isAdmin: false
  }),
  mounted() {
    if (this.$route.matched[0].name == "admin") {
      this.isAdmin = true;
    }
  },
  methods: {
    ...mapActions({
      ORDER_SET_BY_INDEX: "ORDER_SET_BY_INDEX"
    }),
    ...mapGetters({
      getOrders: "getOrders",
      getUser: "getUser"
    }),
    onEditSchedule(content) {
      axios
        .get(`/products?id=${content.data.Product.id}`)
        .then(response => {
          this.orderIndex = content.index;
          this.orderData = content.data;
          const product = response.data.data[0];
          this.modalData = {
            productId: product.id,
            categoryId: product.categoryId,
            locationId: product.locationId,
            item: {
              ...product,
              label: product.name,
              image: product.image,
              id: product.id,
              locationId: product.locationId,
              location: product.Location,
              // TODO: Additional capacity from source
              category: product.Category
            }
          };
          document.body.style.overflow = "hidden";
          this.isOpenNewBooking = true;
        })
        .catch(err => {
          console.log(err);
        });
    },
    handleLoading(isLoading) {
      this.isLoading = isLoading;
    },
    openMakeBooking() {
      this.isOpenMakeBooking = true;
    },
    onCloseModal() {
      document.body.style.overflow = null;
      this.orderData = null;
      this.orderIndex = null;
      this.isOpenNewBooking = false;
    },
    onSubmitModal(schedule) {
      this.ORDER_SET_BY_INDEX({ index: this.orderIndex, order: schedule });
      this.onCloseModal();
    }
  }
};
</script>

<style lang="scss" scoped>
.footer-action {
  display: flex;
  flex-flow: row wrap;
  margin: 0;

  .btn {
    flex: 100%;
    margin: 0 0 1rem;
    padding: 0.75rem 1rem;

    @media (min-width: $screen-md) {
      flex: 0;

      ~ .btn {
        margin-left: 1.5rem;
      }
    }

    @media (orientation: landscape) and (min-width: $screen-md) {
      // flex: 1;
    }
  }
}

.header {
  font-family: $font-family-base;
  font-size: 24px;
  font-weight: 500;
  line-height: 26px;
  margin: 0 0 30px;
}

@media (min-width: $screen-md) {
  .header {
    margin: 0 0 30px;
  }
}

.scrollbar--primary {
  max-height: 50vh;
  margin: 0 -0.9375rem 0 0;

  @media (min-width: $screen-md) {
    margin: 0;
    max-height: 50vh;
  }

  @media (min-width: $screen-xl) {
    max-height: 52vh;
  }

  @media (min-width: $screen-xxl) {
    max-height: 55vh;
  }

  @media (orientation: portrait) and (min-width: $screen-md) {
    max-height: 65vh;
  }
}

.no-result {
  margin-bottom: 10px;
  padding: 20px;
  text-align: center;
  img {
    width: 250px;
  }
  p {
    font-size: 24px;
    margin: 40px 0;
  }
}
</style>
